/**
 * Session thunks — boot-time grant fetch.
 *
 * The web admin calls `GET /users/me/organization-grants` on every
 * authenticated app boot (post-login OR session-rehydration). The
 * response shape is `[{ organizationId, organizationName, isActive }]`
 * (see `product_loyalty_backend/src/modules/master/user/user-master/me/users-me.controller.ts`
 * from Stage A / PR #30).
 *
 * A non-Micas admin has zero grants — the endpoint returns `[]` and the
 * `<BrandSwitcher>` stays hidden. Errors are swallowed to state (no
 * toast) — the switcher just doesn't render, which is the correct
 * "wired but latent" behavior for users who don't have grants yet or if
 * the endpoint 404s on an older backend.
 */

import { AppDispatch } from '../index';
import { dataProvider } from '../../api';
import logger from '../../utils/logger';
import {
    grantsFetchStart,
    grantsFetchError,
    setOrgGrants,
    OrgGrant,
} from './reducer';

const GRANTS_ENDPOINT = '/users/me/organization-grants';

export const fetchOrganizationGrants =
    () =>
    async (dispatch: AppDispatch): Promise<OrgGrant[]> => {
        dispatch(grantsFetchStart());
        try {
            const response = await dataProvider.get<OrgGrant[] | { data: OrgGrant[] }>(
                GRANTS_ENDPOINT
            );
            // The apiClient response interceptor already unwraps the
            // backend's `{ statusCode, status, data }` envelope, so `get`
            // typically returns the payload directly. Guard against both
            // shapes anyway.
            const grants: OrgGrant[] = Array.isArray(response)
                ? response
                : Array.isArray((response as any)?.data)
                ? (response as any).data
                : [];
            dispatch(setOrgGrants(grants));
            return grants;
        } catch (err: any) {
            const message = err?.message || 'Failed to load organization grants';
            logger.warn('fetchOrganizationGrants failed (silently handled)', err);
            dispatch(grantsFetchError(message));
            // Do NOT re-throw. Grant fetch is best-effort — an older
            // backend without PR #30 will 404 and every non-Micas admin
            // should still see a working shell.
            return [];
        }
    };
